/**
 * What a {@link FunnelLook} means to a Rive rig, slot by slot.
 *
 * Two stages paint the child from the same look: `AvatarStage` (the editing
 * artboard, captured to PNGs for the posters) and `CartoonPlayer` (the story
 * artboards of the cartoon). Their rigs name their image slots differently, so
 * the plan takes the slot names as an argument — but what goes INTO a slot for
 * a given look is decided here, once, so the poster child and the cartoon
 * child can never be dressed differently.
 */
import { COLOR_PROP_META, EYE_COLOURS, REFERENCED_ASSET_URLS } from '@editor/contract';
import {
  FRECKLES_IMAGES,
  GLASSES_IMAGES,
  HAIR_BACK,
  HAIR_FRONT,
  PUPIL_IMAGES,
  type AuthoredPalettes,
} from '@editor/editorAssets';
import { buildBlankLayer, buildRecoloredHair, type HairLayer } from '@editor/hairCompositor';
import { hexToRgb } from '@editor/colorUtils';
import type { FunnelLook } from '../avatarLook';

/** Rive image-asset names of the child's slots in one rig. */
export interface RigSlots {
  pupil?: string;
  freckles?: string;
  glasses?: string;
  hairFront?: string;
  hairBack?: string;
}

/** What one image slot must hold for a look; `sig` is what an apply records when it starts the swap. */
export interface SlotPlan {
  kind: 'image' | 'hair';
  slot: string | undefined;
  /** Undefined when the look wants nothing for this slot. */
  sig: string | undefined;
  /** Hair only: the grayscale stack to recolour; empty means a blank layer. */
  layers: HairLayer[];
}

/**
 * The slot-by-slot contract between a look and a rig, used twice: an apply
 * walks it to push images, and a capture walks it to know when they have all
 * landed. One function, so the two can never disagree about what "applied"
 * means.
 */
export function slotPlan(look: FunnelLook, palettes: AuthoredPalettes | null, slots: RigSlots): SlotPlan[] {
  const pupilStem = EYE_COLOURS[look.eye]?.name;
  const image = (slot: string | undefined, url: string | undefined): SlotPlan => ({
    kind: 'image', slot, sig: slot ? (url ?? REFERENCED_ASSET_URLS[slot]) : undefined, layers: [],
  });
  const shades = palettes?.hairShades[look.hair] ?? [];
  const hairLayer = (slot: string | undefined, style: string | null, layers: HairLayer[]): SlotPlan => ({
    kind: 'hair', slot, sig: !(style && layers.length) ? 'none' : `${style}|${shades.join(',')}`, layers,
  });
  return [
    image(slots.pupil,
      (pupilStem ? PUPIL_IMAGES.byStem[pupilStem] : undefined)
        ?? (pupilStem ? PUPIL_IMAGES.byStem[pupilStem.toLowerCase()] : undefined)),
    image(slots.freckles, look.frecklesStyle ? FRECKLES_IMAGES.byStem[look.frecklesStyle] : undefined),
    image(slots.glasses, look.glassesStyle ? GLASSES_IMAGES.byStem[look.glassesStyle] : undefined),
    hairLayer(slots.hairFront, look.frontStyle, look.frontStyle ? HAIR_FRONT.byStyle[look.frontStyle] ?? [] : []),
    hairLayer(slots.hairBack, look.backStyle, look.backStyle ? HAIR_BACK.byStyle[look.backStyle] ?? [] : []),
  ];
}

/** The PNG bytes a slot-plan entry wants in its slot, or null for "leave it". */
export async function slotBytes(entry: SlotPlan, palettes: AuthoredPalettes, look: FunnelLook): Promise<Uint8Array | null> {
  if (!entry.sig) return null;
  if (entry.kind === 'image') {
    const res = await fetch(encodeURI(entry.sig));
    if (!res.ok) return null;
    return new Uint8Array(await res.arrayBuffer());
  }
  if (entry.sig === 'none') return buildBlankLayer();
  return buildRecoloredHair(entry.layers, palettes.hairShades[look.hair] ?? []);
}

/** The bound colours a look asks for: the tone's skin set plus the hair colour's eyebrow. */
export function lookColours(look: FunnelLook, palettes: AuthoredPalettes): Record<string, string> {
  return { ...(palettes.skinSets[look.tone] ?? {}), eyebrow: palettes.hairEyebrow[look.hair] };
}

/** The booleans the rig binds. */
export function lookBools(look: FunnelLook): Record<string, boolean> {
  return { isGirl: look.isGirl, frecklesOn: !!look.frecklesStyle, glassesOn: !!look.glassesStyle };
}

/** The shape of a bound ViewModel instance this needs — the runtime's, structurally. */
export interface LookViewModel {
  properties: { name: string }[];
  color(name: string): { rgb(r: number, g: number, b: number): void } | null | undefined;
  boolean(name: string): { value: boolean } | null | undefined;
}

/**
 * Write a look's colours and booleans into a ViewModel instance. Property
 * names are matched exactly first, then case-insensitively — the child's
 * nested VM in the story file carries the same canonical names as the editing
 * rig, and a property the instance does not expose is skipped.
 */
export function writeLook(vmi: LookViewModel | null | undefined, look: FunnelLook, palettes: AuthoredPalettes): void {
  if (!vmi) return;
  let available: string[] = [];
  try {
    available = vmi.properties.map((p) => p.name);
  } catch {
    return; // disposed
  }
  const resolve = (canonical: string) =>
    available.find((n) => n === canonical) ?? available.find((n) => n.toLowerCase() === canonical.toLowerCase());
  const colours = lookColours(look, palettes);
  for (const canonical of Object.keys(COLOR_PROP_META)) {
    const hex = colours[canonical];
    const name = hex ? resolve(canonical) : undefined;
    if (!name) continue;
    const rgb = hexToRgb(hex);
    if (!rgb) continue;
    try {
      vmi.color(name)?.rgb(rgb.r, rgb.g, rgb.b);
    } catch {
      /* not bound in this file */
    }
  }
  for (const [canonical, value] of Object.entries(lookBools(look))) {
    const name = resolve(canonical);
    if (!name) continue;
    try {
      const b = vmi.boolean(name);
      if (b) b.value = value;
    } catch {
      /* not bound */
    }
  }
}
